import React, { useState, useEffect } from "react";
import {
  Brain, TrendingDown, Lightbulb, Link2, Target,
  RefreshCw, X, Check, ArrowRight, ChevronDown, ChevronRight,
  Zap, AlertTriangle, Plus, Eye, FileText, ExternalLink,
  HelpCircle, Copy, Download, Loader,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import ScoreRing from "./ScoreRing";
import KeywordDetailModal, { buildPageGeneratorUrl, parseKeywordLocation } from "./KeywordDetailModal";

function TabButton({ active, onClick, icon: Icon, label, count }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${active ? "border-copper text-copper" : "border-transparent text-gray-400 hover:text-white"}`}>
      <Icon size={16} /> {label}
      {count > 0 && <span className="text-xs bg-dark-50 px-1.5 py-0.5 rounded">{count}</span>}
    </button>
  );
}

function DecayCard({ decay, onDismiss, onRefresh, onViewOptimizer }) {
  const [expanded, setExpanded] = useState(false);
  const [refreshState, setRefreshState] = useState("idle"); // idle | loading | success | error
  const [refreshMessage, setRefreshMessage] = useState("");

  const handleRefreshClick = async () => {
    if (refreshState === "loading") return;
    setRefreshState("loading");
    setRefreshMessage("");
    try {
      await onRefresh(decay.id);
      setRefreshState("success");
      setRefreshMessage("Content refresh started — check back in a few minutes");
      setTimeout(() => setRefreshState("idle"), 6000);
    } catch (err) {
      setRefreshState("error");
      setRefreshMessage(err?.response?.data?.error || "Failed to start refresh");
      setTimeout(() => setRefreshState("idle"), 6000);
    }
  };

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-gray-200 font-mono truncate">{decay.page_url.replace(/^https?:\/\//, "")}</div>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-red-400 font-mono">▼ {decay.position_drop.toFixed(1)} positions</span>
            <span className="text-xs text-gray-500">{decay.avg_position_before.toFixed(1)} → {decay.avg_position_now.toFixed(1)}</span>
          </div>
          <div className="flex gap-2 mt-2 items-center flex-wrap">
            <button
              onClick={handleRefreshClick}
              disabled={refreshState === "loading"}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors disabled:opacity-60"
            >
              {refreshState === "loading" ? <Loader size={11} className="animate-spin" /> : <Zap size={11} />}
              {refreshState === "loading" ? "Starting refresh..." : "Refresh Content"}
            </button>
            <button
              onClick={() => onViewOptimizer(decay.page_url)}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-50 text-gray-300 rounded hover:bg-dark-50/80 transition-colors"
            >
              <Eye size={11} /> View in SEO Optimizer
            </button>
            {refreshState === "success" && (
              <span className="flex items-center gap-1 text-xs text-green-400">
                <Check size={11} /> {refreshMessage}
              </span>
            )}
            {refreshState === "error" && (
              <span className="flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle size={11} /> {refreshMessage}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onDismiss(decay.id)} className="p-1.5 text-gray-500 hover:text-gray-300 rounded" title="Dismiss">
            <X size={14} />
          </button>
        </div>
      </div>
      {decay.ai_recommendations?.length > 0 && (
        <div>
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-gray-400 flex items-center gap-1 hover:text-white">
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {decay.ai_recommendations.length} recommendations
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1">
              {decay.ai_recommendations.map((r, i) => (
                <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                  <span className="text-copper mt-0.5">•</span> {r}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function KeywordCard({ kw, onAccept, onDismiss, onCreatePage, onViewDetail }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => onViewDetail(kw.keyword)} className="text-sm font-mono text-gray-200 hover:text-copper transition-colors text-left">
            {kw.keyword}
          </button>
          <div className="flex items-center gap-3 mt-1.5">
            <span className={`text-xs px-1.5 py-0.5 rounded ${kw.competition_level === "low" ? "bg-green-900/20 text-green-400" : kw.competition_level === "medium" ? "bg-amber-900/20 text-amber-400" : "bg-red-900/20 text-red-400"}`}>
              {kw.competition_level}
            </span>
            {kw.already_ranking && <span className="text-xs text-gray-500">Pos #{kw.our_current_position}</span>}
            {!kw.already_ranking && <span className="text-xs text-blue-400">Not ranking</span>}
          </div>
          <div className="text-xs text-gray-500 mt-1">{kw.reasoning}</div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => onCreatePage(kw.keyword)}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors"
            >
              <FileText size={11} /> Create Page
            </button>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2">
          <ScoreRing score={kw.opportunity_score} size={40} strokeWidth={3} />
          <div className="flex gap-1">
            <button onClick={() => onAccept(kw.id)} className="p-1 text-green-400 hover:bg-green-900/30 rounded" title="Accept">
              <Check size={12} />
            </button>
            <button onClick={() => onDismiss(kw.id)} className="p-1 text-gray-500 hover:text-gray-300 rounded" title="Dismiss">
              <X size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkCard({ link, selectedSite, onDone }) {
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(link.status === "applied" || link.status === "added");
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const handleAddLink = () => {
    setPushing(true);
    setError(null);
    setSuggestions([]);
    api.post(`/websites/${selectedSite.id}/internal-links/${link.id}/apply/`)
      .then(() => { setPushed(true); if (onDone) onDone(); })
      .catch((err) => {
        const data = err.response?.data || {};
        setError(data.error || "Failed to apply link");
        if (data.suggestions?.length) setSuggestions(data.suggestions);
      })
      .finally(() => setPushing(false));
  };

  const formatUrl = (url) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="flex items-start gap-3 mb-3">
        {/* Source page */}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Source Page</div>
          <div className="text-sm text-gray-200 leading-snug">{link.source_title || formatUrl(link.source_url)}</div>
          <a href={link.source_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-500 hover:text-copper transition-colors flex items-center gap-1 mt-0.5">
            {formatUrl(link.source_url)} <ExternalLink size={9} />
          </a>
        </div>
        {/* Arrow */}
        <ArrowRight size={16} className="text-copper flex-shrink-0 mt-4" />
        {/* Target page */}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Target Page</div>
          <div className="text-sm text-gray-200 leading-snug">{link.target_title || formatUrl(link.target_url)}</div>
          <a href={link.target_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-500 hover:text-copper transition-colors flex items-center gap-1 mt-0.5">
            {formatUrl(link.target_url)} <ExternalLink size={9} />
          </a>
        </div>
      </div>
      <div className="text-xs text-copper font-mono mb-1">Anchor: "{link.anchor_text}"</div>
      {link.ai_reasoning && <div className="text-xs text-gray-500">{link.ai_reasoning}</div>}
      {error && (
        <div className="mt-2 text-xs text-amber-400 bg-amber-900/15 rounded-lg px-3 py-2">
          <div className="flex items-center gap-1 font-medium"><AlertTriangle size={11} /> {error}</div>
          {suggestions.length > 0 && (
            <div className="mt-1 text-gray-400">
              Phrases found on page: {suggestions.map((s, i) => (
                <span key={i} className="text-amber-300 font-mono">"{s}"{i < suggestions.length - 1 ? ", " : ""}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {!pushed && (
        <button
          onClick={handleAddLink}
          disabled={pushing}
          className="mt-2 flex items-center gap-1 px-2 py-1 text-xs bg-blue-900/20 text-blue-400 rounded hover:bg-blue-900/30 transition-colors disabled:opacity-50"
        >
          <Link2 size={11} /> {pushing ? "Adding..." : "Add Link to WP"}
        </button>
      )}
      {pushed && (
        <span className="mt-2 inline-flex items-center gap-1 text-xs text-green-400">
          <Check size={11} /> Applied
        </span>
      )}
    </div>
  );
}

function SerpFeatureRow({ feature, onClickKeyword, onGenerateFaq, selectedSite }) {
  const icons = {
    featured_snippet: "⭐",
    people_also_ask: "❓",
    local_pack: "📍",
    knowledge_panel: "📋",
    video_carousel: "🎥",
    image_pack: "🖼️",
  };
  const isPaa = feature.feature_type === "people_also_ask";

  return (
    <tr className="border-b border-dark-50/50 hover:bg-dark-50/30">
      <td className="py-2.5 px-4 font-mono text-sm text-gray-300">
        <button onClick={() => onClickKeyword(feature.keyword)} className="hover:text-copper transition-colors text-left">
          {feature.keyword}
        </button>
      </td>
      <td className="py-2.5 px-3 text-sm">
        <span className="mr-1">{icons[feature.feature_type] || ""}</span>
        {feature.feature_type.replace(/_/g, " ")}
      </td>
      <td className="py-2.5 px-3">
        {feature.currently_winning ? (
          <span className="text-xs bg-green-900/20 text-green-400 px-2 py-0.5 rounded">Winning</span>
        ) : (
          <span className="text-xs bg-amber-900/20 text-amber-400 px-2 py-0.5 rounded">Opportunity</span>
        )}
      </td>
      <td className="py-2.5 px-3 text-xs text-gray-400 max-w-xs truncate">{feature.optimization_tip}</td>
      <td className="py-2.5 px-3">
        {isPaa && !feature.currently_winning && (
          <button
            onClick={() => onGenerateFaq(feature)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-900/20 text-purple-400 rounded hover:bg-purple-900/30 transition-colors whitespace-nowrap"
          >
            <HelpCircle size={11} /> Generate FAQ
          </button>
        )}
      </td>
    </tr>
  );
}

// --- FAQ Modal Component ---
function FaqModal({ feature, selectedSite, onClose }) {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQs, setSelectedQs] = useState(new Set());
  const [mode, setMode] = useState("page"); // page | existing | schema
  const [schemaResult, setSchemaResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [wpPages, setWpPages] = useState([]);
  const [selectedWpPage, setSelectedWpPage] = useState("");

  useEffect(() => {
    if (!feature || !selectedSite) return;

    // Fetch PAA questions
    api.get(`/websites/${selectedSite.id}/serp-features/${feature.id}/paa-questions/`)
      .then((res) => {
        const qs = res.data.questions || [];
        setQuestions(qs);
        setSelectedQs(new Set(qs.map((_, i) => i)));
      })
      .catch(() => setQuestions([]))
      .finally(() => setLoading(false));

    // Fetch existing WordPress pages for "add to existing" option
    api.get(`/websites/${selectedSite.id}/pages/`, { params: { status: "published" } })
      .then((res) => {
        const pages = res.data.results || res.data;
        setWpPages(pages.filter((p) => p.wp_post_id));
      })
      .catch(() => setWpPages([]));
  }, [feature, selectedSite]);

  const toggleQ = (idx) => {
    const next = new Set(selectedQs);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setSelectedQs(next);
  };

  const getSelectedQuestions = () => questions.filter((_, i) => selectedQs.has(i));

  const handleCreateFaqPage = () => {
    const qs = getSelectedQuestions();
    const { service, location } = parseKeywordLocation(feature.keyword);
    const params = new URLSearchParams();
    params.set("action", "generate");
    params.set("keyword", service);
    if (location) params.set("location", location);
    params.set("type", "faq");
    params.set("paa_questions", JSON.stringify(qs.map((q) => typeof q === "string" ? q : q.question)));
    onClose();
    navigate(`/pages?${params.toString()}`);
  };

  const handleAddToExisting = async () => {
    if (!selectedWpPage) return;
    setGenerating(true);
    try {
      const qs = getSelectedQuestions();
      await api.post(`/websites/${selectedSite.id}/serp-features/add-faq-to-page/`, {
        questions: qs,
        keyword: feature.keyword,
        wp_page_id: parseInt(selectedWpPage),
      });
      onClose();
    } catch (err) {
      // Show error inline
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSchema = async () => {
    setGenerating(true);
    try {
      const qs = getSelectedQuestions();
      const res = await api.post(`/websites/${selectedSite.id}/serp-features/generate-faq-schema/`, {
        questions: qs,
        keyword: feature.keyword,
      });
      setSchemaResult(res.data);
    } catch (err) {
      // Show error inline
    } finally {
      setGenerating(false);
    }
  };

  const copySchema = () => {
    if (schemaResult?.schema_json) {
      navigator.clipboard.writeText(schemaResult.schema_json);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-dark-50">
          <div>
            <h3 className="text-base font-semibold">Generate FAQ Content</h3>
            <div className="text-xs text-gray-500 mt-0.5 font-mono">{feature.keyword}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-dark-50/50 rounded-lg transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="py-8 text-center text-gray-500 animate-pulse">Fetching PAA questions...</div>
          ) : questions.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No PAA questions found for this keyword</div>
          ) : (
            <>
              {/* Question list with checkboxes */}
              <div>
                <div className="text-xs text-gray-500 font-medium mb-2">
                  People Also Ask ({selectedQs.size} of {questions.length} selected)
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {questions.map((q, i) => (
                    <label key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-dark-50/30 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedQs.has(i)}
                        onChange={() => toggleQ(i)}
                        className="mt-0.5 w-4 h-4 rounded border-dark-50 bg-dark text-copper focus:ring-copper/50"
                      />
                      <div>
                        <div className="text-sm text-gray-200">{typeof q === "string" ? q : q.question}</div>
                        {q.snippet && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{q.snippet}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Mode selection */}
              <div>
                <div className="text-xs text-gray-500 font-medium mb-2">Action</div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setMode("page")}
                    className={`text-left p-3 rounded-lg border text-xs transition-colors ${mode === "page" ? "border-copper bg-copper/10 text-copper" : "border-dark-50 text-gray-400 hover:border-gray-600"}`}
                  >
                    <FileText size={14} className="mb-1" />
                    Create new FAQ Landing Page
                  </button>
                  <button
                    onClick={() => setMode("existing")}
                    className={`text-left p-3 rounded-lg border text-xs transition-colors ${mode === "existing" ? "border-copper bg-copper/10 text-copper" : "border-dark-50 text-gray-400 hover:border-gray-600"}`}
                  >
                    <Plus size={14} className="mb-1" />
                    Add FAQ section to existing page
                  </button>
                  <button
                    onClick={() => setMode("schema")}
                    className={`text-left p-3 rounded-lg border text-xs transition-colors ${mode === "schema" ? "border-copper bg-copper/10 text-copper" : "border-dark-50 text-gray-400 hover:border-gray-600"}`}
                  >
                    <Copy size={14} className="mb-1" />
                    Generate FAQ Schema only
                  </button>
                </div>
              </div>

              {/* Mode-specific content */}
              {mode === "existing" && (
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Select a published page</label>
                  <select
                    value={selectedWpPage}
                    onChange={(e) => setSelectedWpPage(e.target.value)}
                    className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
                  >
                    <option value="">Choose a page...</option>
                    {wpPages.map((p) => (
                      <option key={p.id} value={p.wp_post_id}>
                        {p.title || p.target_keyword} ({p.target_location || "no location"})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {mode === "schema" && schemaResult && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 font-medium">FAQPage JSON-LD Schema</span>
                    <button onClick={copySchema} className="text-xs text-copper hover:underline flex items-center gap-1">
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                  <pre className="bg-dark rounded-lg p-3 text-xs text-gray-300 font-mono max-h-60 overflow-auto whitespace-pre-wrap">
                    {schemaResult.schema_json}
                  </pre>
                </div>
              )}

              {/* Action button */}
              <div className="flex gap-3">
                {mode === "page" && (
                  <button
                    onClick={handleCreateFaqPage}
                    disabled={selectedQs.size === 0}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    <FileText size={14} />
                    Create FAQ Landing Page
                  </button>
                )}
                {mode === "existing" && (
                  <button
                    onClick={handleAddToExisting}
                    disabled={selectedQs.size === 0 || !selectedWpPage || generating}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {generating ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
                    {generating ? "Adding FAQ..." : "Add FAQ to Page"}
                  </button>
                )}
                {mode === "schema" && !schemaResult && (
                  <button
                    onClick={handleGenerateSchema}
                    disabled={selectedQs.size === 0 || generating}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {generating ? <Loader size={14} className="animate-spin" /> : <Copy size={14} />}
                    {generating ? "Generating..." : "Generate Schema"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AiInsights({ selectedSite }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("decay");
  const [decay, setDecay] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [links, setLinks] = useState([]);
  const [serp, setSerp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState("");
  const [detailKeyword, setDetailKeyword] = useState(null);
  const [faqFeature, setFaqFeature] = useState(null);

  const fetchAll = () => {
    if (!selectedSite) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${selectedSite.id}/content-decay/`).then((r) => setDecay(r.data.results || r.data)).catch(() => setDecay([])),
      api.get(`/websites/${selectedSite.id}/suggested-keywords/`).then((r) => setKeywords(r.data.results || r.data)).catch(() => setKeywords([])),
      api.get(`/websites/${selectedSite.id}/internal-links/`).then((r) => setLinks(r.data.results || r.data)).catch(() => setLinks([])),
      api.get(`/websites/${selectedSite.id}/serp-features/`).then((r) => setSerp(r.data.results || r.data)).catch(() => setSerp([])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, [selectedSite]);

  const runAction = (endpoint, label) => {
    setRunning(label);
    api.post(`/websites/${selectedSite.id}/${endpoint}/`)
      .then(() => setTimeout(fetchAll, 3000))
      .finally(() => setRunning(""));
  };

  const decayCount = decay.filter((d) => d.status === "decaying").length;
  const kwCount = keywords.filter((k) => k.status === "suggested").length;
  const linkCount = links.filter((l) => l.status === "suggested").length;

  if (!selectedSite) return <div className="text-gray-500 text-center py-20">Select a website</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Brain size={20} className="text-copper" /> AI Insights
        </h2>
        <div className="flex gap-2">
          <button onClick={() => runAction("content-decay/detect", "decay")} disabled={!!running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-300 hover:text-white disabled:opacity-50">
            <RefreshCw size={12} className={running === "decay" ? "animate-spin" : ""} /> Detect Decay
          </button>
          <button onClick={() => runAction("suggested-keywords/discover", "keywords")} disabled={!!running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-300 hover:text-white disabled:opacity-50">
            <RefreshCw size={12} className={running === "keywords" ? "animate-spin" : ""} /> Discover Keywords
          </button>
          <button onClick={() => runAction("internal-links/analyze", "links")} disabled={!!running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-300 hover:text-white disabled:opacity-50">
            <RefreshCw size={12} className={running === "links" ? "animate-spin" : ""} /> Analyze Links
          </button>
          <button onClick={() => runAction("serp-features/scan", "serp")} disabled={!!running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-300 hover:text-white disabled:opacity-50">
            <RefreshCw size={12} className={running === "serp" ? "animate-spin" : ""} /> Scan SERP
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-dark-50">
        <TabButton active={tab === "decay"} onClick={() => setTab("decay")} icon={TrendingDown} label="Content Decay" count={decayCount} />
        <TabButton active={tab === "keywords"} onClick={() => setTab("keywords")} icon={Lightbulb} label="Keyword Discovery" count={kwCount} />
        <TabButton active={tab === "links"} onClick={() => setTab("links")} icon={Link2} label="Internal Links" count={linkCount} />
        <TabButton active={tab === "serp"} onClick={() => setTab("serp")} icon={Target} label="SERP Features" count={serp.length} />
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500 animate-pulse">Loading AI insights...</div>
      ) : (
        <>
          {tab === "decay" && (
            <div className="space-y-3">
              {decay.filter((d) => d.status === "decaying").length === 0 ? (
                <div className="text-gray-500 text-center py-12">No decaying pages detected</div>
              ) : (
                decay.filter((d) => d.status === "decaying").map((d) => (
                  <DecayCard key={d.id} decay={d}
                    onDismiss={(id) => { api.post(`/websites/${selectedSite.id}/content-decay/${id}/dismiss/`).then(fetchAll); }}
                    onRefresh={(id) => api.post(`/websites/${selectedSite.id}/content-decay/${id}/refresh/`)}
                    onViewOptimizer={(pageUrl) => navigate(`/seo-optimizer?url=${encodeURIComponent(pageUrl)}`)}
                  />
                ))
              )}
            </div>
          )}

          {tab === "keywords" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {keywords.filter((k) => k.status === "suggested").length === 0 ? (
                <div className="text-gray-500 text-center py-12 md:col-span-2">No keyword suggestions yet — click "Discover Keywords"</div>
              ) : (
                keywords.filter((k) => k.status === "suggested").map((kw) => (
                  <KeywordCard key={kw.id} kw={kw}
                    onAccept={(id) => { api.patch(`/websites/${selectedSite.id}/suggested-keywords/${id}/`, { status: "accepted" }).then(fetchAll); }}
                    onDismiss={(id) => { api.patch(`/websites/${selectedSite.id}/suggested-keywords/${id}/`, { status: "dismissed" }).then(fetchAll); }}
                    onCreatePage={(keyword) => navigate(buildPageGeneratorUrl(keyword))}
                    onViewDetail={(keyword) => setDetailKeyword(keyword)}
                  />
                ))
              )}
            </div>
          )}

          {tab === "links" && (
            <div className="space-y-3">
              {links.filter((l) => l.status === "suggested").length === 0 ? (
                <div className="text-gray-500 text-center py-12">No link suggestions yet — click "Analyze Links"</div>
              ) : (
                links.filter((l) => l.status === "suggested").map((link) => (
                  <LinkCard key={link.id} link={link} selectedSite={selectedSite} onDone={fetchAll} />
                ))
              )}
            </div>
          )}

          {tab === "serp" && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
              {serp.length === 0 ? (
                <div className="text-gray-500 text-center py-12">No SERP features detected — click "Scan SERP"</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 border-b border-dark-50">
                      <th className="text-left py-2.5 px-4 font-medium">Keyword</th>
                      <th className="text-left py-2.5 px-3 font-medium">Feature</th>
                      <th className="py-2.5 px-3 font-medium">Status</th>
                      <th className="text-left py-2.5 px-3 font-medium">Tip</th>
                      <th className="py-2.5 px-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serp.map((f) => (
                      <SerpFeatureRow
                        key={f.id}
                        feature={f}
                        selectedSite={selectedSite}
                        onClickKeyword={(kw) => setDetailKeyword(kw)}
                        onGenerateFaq={(feature) => setFaqFeature(feature)}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {detailKeyword && (
        <KeywordDetailModal
          keyword={detailKeyword}
          selectedSite={selectedSite}
          onClose={() => setDetailKeyword(null)}
        />
      )}

      {faqFeature && (
        <FaqModal
          feature={faqFeature}
          selectedSite={selectedSite}
          onClose={() => setFaqFeature(null)}
        />
      )}
    </div>
  );
}
